/**
 * Authorization Service
 * Provides API methods for checking permissions and evaluating access control
 */

import type {
  HasPermissionResponse,
  AccessibleResourcesResponse,
  AllowedOperationsResponse,
  PermissionsSummary,
  ResourceType,
  OperationType,
} from '@/types/roles';
import { authService } from './authService';
import { apiRequest } from '@/config/msalConfig';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface ApiErrorResponse {
  message: string;
  details?: string;
}

class ApiError extends Error {
  response: ApiErrorResponse;
  status: number;

  constructor(response: ApiErrorResponse, status: number) {
    super(response.message);
    this.name = 'ApiError';
    this.response = response;
    this.status = status;
  }
}

async function getDefaultHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  const baseHeaders: Record<string, string> = { Accept: 'application/json' };

  // Try to get Bearer token from MSAL if authenticated
  if (authService.isAuthenticated()) {
    try {
      // Request API scope for backend authorization
      const token = await authService.getAccessToken(apiRequest.scopes);
      baseHeaders['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.warn('Failed to get access token for authorization request:', error);
    }
  }

  return { ...baseHeaders, ...extra };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      try {
        const errorData = (await response.json()) as ApiErrorResponse;
        throw new ApiError(errorData, response.status);
      } catch (e) {
        if (e instanceof ApiError) throw e;
        throw new ApiError(
          {
            message: `HTTP ${response.status}: ${response.statusText}`,
            details: 'Malformed JSON error response',
          },
          response.status
        );
      }
    }

    throw new ApiError(
      {
        message: `HTTP ${response.status}: ${response.statusText}`,
        details: 'No JSON error body returned',
      },
      response.status
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return await response.json();
}

export class AuthorizationService {
  /**
   * Check if the current user has permission to perform an operation on a resource
   * Optionally checks permission for a specific path within the resource
   * Requires: Authentication (any role)
   *
   * @param resource - The resource type (e.g., 'presets', 'tenants')
   * @param operation - The operation type (e.g., 'read', 'write')
   * @param path - Optional path within the resource (e.g., 'database', 'configuration.identity')
   */
  async hasPermission(
    resource: ResourceType | string,
    operation: OperationType | string,
    path?: string
  ): Promise<HasPermissionResponse> {
    const params = new URLSearchParams({
      resource,
      operation,
    });

    if (path) {
      params.set('path', path);
    }

    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/authorization/has-permission?${params}`, {
      headers,
    });
    return handleResponse<HasPermissionResponse>(response);
  }

  /**
   * Get all resources accessible to the current user for a specific operation
   * Returns list of resources and their accessible paths
   * Requires: Authentication (any role)
   *
   * @param operation - The operation type (e.g., 'read', 'write')
   */
  async getAccessibleResources(
    operation: OperationType | string
  ): Promise<AccessibleResourcesResponse> {
    const params = new URLSearchParams({ operation });

    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/authorization/accessible-resources?${params}`, {
      headers,
    });
    return handleResponse<AccessibleResourcesResponse>(response);
  }

  /**
   * Get all operations allowed on a specific resource
   * Optionally checks for a specific path within the resource
   * Requires: Authentication (any role)
   *
   * @param resource - The resource type (e.g., 'presets', 'tenants')
   * @param path - Optional path within the resource (e.g., 'database', 'configuration.identity')
   */
  async getAllowedOperations(
    resource: ResourceType | string,
    path?: string
  ): Promise<AllowedOperationsResponse> {
    const params = new URLSearchParams({ resource });

    if (path) {
      params.set('path', path);
    }

    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/authorization/allowed-operations?${params}`, {
      headers,
    });
    return handleResponse<AllowedOperationsResponse>(response);
  }

  /**
   * Get the current user's permissions summary
   * Includes all assigned roles and combined scopes
   * Requires: Authentication (any role)
   */
  async getCurrentUserPermissions(): Promise<PermissionsSummary> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/authorization/me`, {
      headers,
    });
    return handleResponse<PermissionsSummary>(response);
  }

  /**
   * Check if the current user can perform a write operation on a specific configuration path
   * This is a convenience method for the common use case of checking write permissions
   *
   * @param resource - The resource type (e.g., 'presets', 'tenants')
   * @param path - The configuration path (e.g., 'database.connectionString', 'Licensing.maxUsers')
   */
  async canWritePath(resource: ResourceType | string, path: string): Promise<boolean> {
    const result = await this.hasPermission(resource, 'write', path);
    return result.hasPermission;
  }

  /**
   * Check if the current user can read a specific resource
   * This is a convenience method for the common use case of checking read permissions
   *
   * @param resource - The resource type (e.g., 'presets', 'tenants')
   */
  async canRead(resource: ResourceType | string): Promise<boolean> {
    const result = await this.hasPermission(resource, 'read');
    return result.hasPermission;
  }

  /**
   * Check if the current user can write to a specific resource
   * This is a convenience method for the common use case of checking write permissions
   *
   * @param resource - The resource type (e.g., 'presets', 'tenants')
   */
  async canWrite(resource: ResourceType | string): Promise<boolean> {
    const result = await this.hasPermission(resource, 'write');
    return result.hasPermission;
  }
}

// Export singleton instance
export const authorizationService = new AuthorizationService();
